'use client';
import useDonateDialogStore from '@/components/store/donateDialogSlice';
import React, { useState, useEffect, useMemo } from 'react';
import GiftOfGivingDonationForm from './GiftOfGivingDonationForm';
import Dialog from '@ui/Dialog';
import {
  makeGiftOfGivingDonation,
  raiseDonationIntent,
  raiseDonationIntentPublic,
  raiseRecurringDonationIntent,
} from '@/components/store/donate.actions';
import NonProfitProfileBar from './NonProfitProfileBar';
import useUserProfile from '@/hooks/useUserProfile';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import CircularLoading from '@/components/CircularLoader/CircularLoader';
import ActivitySuccessDialog from '../ActivitySuccessDialog';
import { hasAppAccess } from '@/stores/userActions';
import { AppPreferenceItem } from '@/utils/appPreference';
import { differenceInDays } from 'date-fns';
import ExternalDonationForm from './ExternalDonationForm';
import { fetchViaProxy } from '@/actions/proxy-fetch';
import { fetchNonProfitById, fetchPostById } from '@/actions/server.actions';
import { useUpdateFundraiserDonation } from '@/actions/feed.actions';
import Button from '@ui/Button';
import { cx } from 'cva';

const tabs = [{ name: 'Gift of Giving' }, { name: 'Make a Donation' }];

const isProgramExpired = (program) => {
  const diffInDays = differenceInDays(new Date(program?.endDate), new Date());
  return diffInDays < 0;
};

function DonateDialog(props) {
  const isOpen = useDonateDialogStore((state) => state.open);
  const nonProfit = useDonateDialogStore((state) => state.nonProfit);
  const amount = useDonateDialogStore((state) => state.amount);
  const post = useDonateDialogStore((state) => state.post);
  const setIsOpen = useDonateDialogStore((state) => state.setOpen);

  const user = useUserProfile();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [showLoader, setShowLoader] = useState(false);
  const [showSuccess, setShowSuccess] = useState(null);
  const [selectedNonProfit, setSelectedNonProfit] = useState(null);
  const [externalDonationIntent, setExternalDonationIntent] = useState(null);

  const hasGiftOfGivingAccess =
    hasAppAccess(AppPreferenceItem.GiftOfGiving) &&
    user?.giftOfGiving &&
    !isProgramExpired(user?.giftOfGiving?.program);

  const { mutate: updateFundraiserDonation } = useUpdateFundraiserDonation();

  const [selectedTab, setSelectedTab] = useState(tabs[0]);

  const hasDonationUrl = !!nonProfit?.donation_url;

  const memoizedParams = useMemo(() => {
    return new URLSearchParams(searchParams);
  }, [searchParams]);

  function closeModal() {
    if (externalDonationIntent) {
      setExternalDonationIntent(null);
      // TODO: check
      // router.replace('/home');
      // const params = new URLSearchParams(searchParams);
      // params.delete('intentId');
      // const updatedSearchParams = params.toString();
      // router.replace(pathname + '?' + updatedSearchParams, {
      //   scroll: false,
      // });
      router.replace(pathname);
      // Create a new URLSearchParams object based on the current search params
      // const params = new URLSearchParams(searchParams.toString());

      // // Remove the 'intentId' query parameter
      // params.delete('intentId');

      // // Construct the new URL with the updated search params
      // const newUrl = `${pathname}?${params.toString()}`;

      // // Update the URL in the browser without reloading the page
      // router.replace(newUrl, { shallow: true });
    }

    setIsOpen(false);
    setSelectedTab(tabs[0]);
    setShowSuccess(false);
  }

  const isLoggedIn = () => {
    return user?.id ? user.id > 0 : false;
  };

  async function onSubmit(data, isRecurring = false) {
    let payload = {
      ...data,
      tip: (data.amount * data.tip[0]) / 1000,
    };

    if (user?.id !== -1) {
      payload = {
        ...payload,
        firstName: user?.firstName || user.first_name,
        lastName: user?.lastName || user.last_name,
        email: user?.email,
      };
    }

    setShowLoader(true);
    if (isRecurring) {
      const res = await raiseRecurringDonationIntent(
        nonProfit,
        payload,
        post,
        user,
      );
      if (res) {
        if (res?.intentId) {
          router.push(`/payment-success?intentId=${res.intentId}`);
          closeModal();
        } else if (res.approvalLink) {
          window.open(res.approvalLink, '_self');
        }
      }
    } else {
      if (isLoggedIn()) {
        const res = await raiseDonationIntent(nonProfit, payload, post);
        if (res) {
          window.open(res.approvalLink, '_self');
        }
      } else {
        const res = await raiseDonationIntentPublic(nonProfit, payload, post);
        if (res) {
          window.open(res.approvalLink, '_self');
        }
      }
    }
    setShowLoader(false);
  }

  const handleUpdateFundraiserDonation = (amount) => {
    updateFundraiserDonation({
      postId: post.id,
      amount: amount,
      donor: user,
    });
  };

  async function onSubmitGiftOfGivingDonation(data) {
    let payload = {
      ...data,
      firstName: user?.firstName || user.first_name,
      lastName: user?.lastName || user.last_name,
      email: user?.email,
    };

    setShowLoader(true);
    const res = makeGiftOfGivingDonation(nonProfit, payload, post, user);
    if (res) {
      setShowSuccess('gift-of-giving');
      handleUpdateFundraiserDonation(payload.amount);
    }
    setShowLoader(false);
  }

  async function handleFetchExternalDonationPledgeIntent(
    intentId,
    pendingTask = true,
  ) {
    const { ok, data } = await fetchViaProxy(
      `/proxy/v2/protected/users/me/external/donations/intent/${intentId}`,
    );

    if (!ok) {
      console.log('intent not found');
      return;
    }

    const res = data?.data;

    if (res?.userId !== user?.id) {
      router.replace('/api/auth/logout');
    }

    setExternalDonationIntent(res);

    if (!pendingTask) {
      return;
    }

    const nonProfitId = res?.nonProfitId;
    const postId = res?.postId;
    const amount = res?.amount;

    const [nonProfitById, postById] = await Promise.all([
      fetchNonProfitById(nonProfitId),
      fetchPostById(postId),
    ]);

    setSelectedNonProfit(nonProfitById);

    setIsOpen(true, {
      nonProfit: nonProfitById,
      amount: amount,
      post: postById,
    });
  }

  useEffect(() => {
    if (isLoggedIn()) {
      const intentId = memoizedParams.get('intentId');

      if (!externalDonationIntent && memoizedParams.get('intentId')) {
        handleFetchExternalDonationPledgeIntent(intentId);
      }

      if (hasGiftOfGivingAccess && !externalDonationIntent) {
        setSelectedTab(tabs[0]);
      }
    }
  }, []);

  useEffect(() => {
    if (externalDonationIntent) {
      setSelectedTab(tabs[1]);
    }
  }, [selectedNonProfit, externalDonationIntent]);

  const renderTabs = () => {
    if (externalDonationIntent) {
      return null;
    }
    return (
      <div className="mt-1 flex gap-4">
        {hasGiftOfGivingAccess && (
          <Button
            onClick={() => setSelectedTab(tabs[0])}
            variant={
              selectedTab?.name === 'Gift of Giving' ? 'filled' : 'outlined'
            }
            color="transparent"
            className={cx(
              selectedTab?.name === 'Gift of Giving'
                ? 'bg-primary text-white'
                : 'border-1 border-solid border-gray-200 text-primary',
              'border border-primary text-16 font-medium tracking-regular hover:bg-primary hover:text-white',
            )}>
            Gift of Giving
          </Button>
        )}
        {hasDonationUrl && (
          <Button
            onClick={() => setSelectedTab(tabs[1])}
            variant={
              selectedTab?.name === 'Make a Donation' ? 'filled' : 'outlined'
            }
            color="transparent"
            className={cx(
              selectedTab?.name === 'Make a Donation'
                ? 'bg-primary text-white'
                : 'border-1 border-solid border-gray-200 text-primary',
              'border border-primary text-16 font-medium tracking-regular hover:bg-primary hover:text-white',
            )}>
            Make a Donation
          </Button>
        )}
      </div>
    );
  };

  const renderDonationForm = () => {
    // return hasDonationUrl ? (
    //   <ExternalDonationForm
    //     user={user}
    //     post={post}
    //     amount={amount || ''}
    //     nonProfit={nonProfit}
    //     isLoggedIn={isLoggedIn}
    //     handleClose={closeModal}
    //     setShowSuccess={setShowSuccess}
    //     donationIntent={externalDonationIntent}
    //     handleFetchDonationIntent={handleFetchExternalDonationPledgeIntent}
    //   />
    // ) : (
    //   <PaypalDonationForm
    //     post={post}
    //     onSubmit={onSubmit}
    //     amount={amount || ''}
    //     nonProfit={nonProfit}
    //     isLoggedIn={isLoggedIn}
    //   />
    // );

    return (
      <ExternalDonationForm
        user={user}
        post={post}
        amount={amount || ''}
        nonProfit={nonProfit}
        isLoggedIn={isLoggedIn}
        handleClose={closeModal}
        setShowSuccess={setShowSuccess}
        donationIntent={externalDonationIntent}
        handleFetchDonationIntent={handleFetchExternalDonationPledgeIntent}
      />
    );
  };

  if (!selectedNonProfit && !isOpen) {
    return null;
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={closeModal}
      maxWidth="lg"
      contentClass="p-0"
      titleClass="px-6 pt-6 lg:px-8 lg:pt-8">
      {showSuccess ? (
        <ActivitySuccessDialog
          donate
          user={user}
          post={post}
          onClose={closeModal}
          nonProfit={nonProfit}
          donateType={showSuccess}
          isLoggedIn={isLoggedIn()}
        />
      ) : (
        <div className="mb-10 mt-4 flex flex-1 flex-col gap-6">
          <NonProfitProfileBar nonProfit={nonProfit} />
          <div className="flex flex-col">
            {hasGiftOfGivingAccess ? (
              <div className="flex w-full flex-col gap-4 px-6 pt-2 lg:px-8 ">
                {renderTabs()}
                {selectedTab?.name === 'Gift of Giving' ? (
                  <GiftOfGivingDonationForm
                    user={user}
                    amount={amount || ''}
                    nonProfit={nonProfit}
                    isLoggedIn={isLoggedIn}
                    onSubmit={onSubmitGiftOfGivingDonation}
                  />
                ) : (
                  <>{renderDonationForm()}</>
                )}
              </div>
            ) : (
              <div className="flex w-full flex-col gap-4 px-6 pt-2 lg:px-8 ">
                {renderTabs()}
                {renderDonationForm()}
              </div>
            )}
          </div>
          <CircularLoading loaderState={showLoader} />
        </div>
      )}
    </Dialog>
  );
}

export default DonateDialog;
